import LoadingSpinner from '@/components/animations/LoadingSpinner/LoadingSpinner';
import Button from '@/components/elements/forms/buttons/Button/Button';
import { capitalizeFirstLetter, classnames } from '@/helpers';
import { _t, getLang } from '@/locale';
import moment from 'moment';
import DayHeader from './components/DayHeader/DayHeader';
import TimeSlot from './components/TimeSlot/TimeSlot';
import WeekHeader from './components/WeekHeader/WeekHeader';
import { PickTimeWeekViewProps, WeekDay } from './PickTimeWeekView.types';

/**
 * `_CVPickTimeWeekView` content view.
 *
 * A 7-column week grid: a sticky DayHeader row over a column of TimeSlots per
 * day, at desktop (wide card) and narrow (still a 7-column grid, vertical
 * scroll). Discount and Sista Minuten are the same component under `variant` —
 * SMT swaps the header copy, and reduced slots render their price in red (driven
 * by the view-model's per-slot `type`), never a separate component.
 *
 * Purely presentational — it renders the typed WeekViewModel from
 * `PickTimeWeekView.helpers.ts` and reports selection/navigation through
 * callbacks; data fetching, the pickHour→checkout handoff and analytics live in
 * the consumer (deferred wiring).
 */
const PickTimeWeekView = ({
  model,
  isNarrow = false,
  variant = 'default',
  onSelectSlot,
  onPrev,
  onNext,
  canGoPrev = true,
  canGoNext = true,
  onJumpToNextAvailable,
  showWaitlist = false,
  onOpenWaitlist,
  regularTimesUrl,
}: PickTimeWeekViewProps) => {
  const { days, state } = model;
  // Format labels in the active app language; the global moment locale is pinned
  // to 'sv' (helpers/date.js), so localize per-instance (mirrors PickTimeCalendar).
  const lang = getLang();
  const fmt = (input: moment.MomentInput, pattern: string) =>
    capitalizeFirstLetter(moment(input).locale(lang).format(pattern));

  const firstDate = days[0]?.date;
  const lastDate = days[days.length - 1]?.date;
  const rangeTitle = firstDate && lastDate ? `${fmt(firstDate, 'D MMM')} - ${fmt(lastDate, 'D MMM')}` : '';

  const isLoading = state === 'loading';
  const isSmt = variant === 'smt';
  const title = isSmt ? _t('features.weekpicker.PickTimeWeekView.smtTitle') : rangeTitle;
  const subtitle = isSmt ? _t('features.weekpicker.PickTimeWeekView.smtSubtitle') : _t('features.weekpicker.subtitle');

  const subLabelFor = (day: WeekDay): string | undefined => {
    switch (day.state) {
      case 'fullyBooked':
        // A day without slots on an open weekday may be booked out, but also an
        // employee's day off or outside a deal or the booking horizon — no copy
        // fits all of them, so the greyed cell carries none.
        return undefined;
      case 'closed':
        return _t('features.weekpicker.DayHeader.closed');
      case 'campaign':
        return _t('features.weekpicker.DayHeader.campaign');
      default:
        // A past day is 'default' too (buildWeek has no past header variant) and
        // has no bookable slots left — show the month rather than "0 tider".
        if (!day.availableCount) return fmt(day.date, 'MMM');
        return _t('features.weekpicker.DayHeader.timesAvailable', { count: day.availableCount });
    }
  };

  const headerRow = (
    <div
      className={classnames(
        'bg-surface_l2 border-border_regular sticky top-0 z-10 grid grid-cols-7 border-b',
        // Desktop draws the hairline below the WeekHeader here, inside the card. Narrow gets
        // it from the grid block's own top edge, which the header sits above rather than in.
        !isNarrow && 'border-t',
      )}>
      {days.map((day, index) => (
        <DayHeader
          key={day.date}
          weekday={day.isToday ? _t('features.weekpicker.DayHeader.today') : fmt(day.date, 'ddd')}
          dateNumber={fmt(day.date, 'D')}
          // A week whose slots have not arrived classifies every day as fully
          // booked or closed, so while loading the sub-label is a skeleton
          // rather than an availability that is unknown.
          subLabel={isLoading ? undefined : subLabelFor(day)}
          isSubLabelLoading={isLoading}
          state={isLoading ? 'default' : day.state}
          isToday={day.isToday}
          isNarrow={isNarrow}
          className={index < days.length - 1 ? 'border-border_regular border-r' : undefined}
        />
      ))}
    </div>
  );

  const columns = (
    <div className="grid flex-1 grid-cols-7">
      {days.map((day, index) => {
        const isUnavailable = day.state === 'fullyBooked' || day.state === 'closed';
        return (
          <div
            key={day.date}
            className={classnames(
              'gap-xs flex flex-col',
              isNarrow ? 'p-2xs' : 'p-xs',
              index < days.length - 1 && 'border-border_regular border-r',
              isUnavailable && 'bg-bg_secondary_neutral',
            )}>
            {day.slots.map((slot) => (
              <TimeSlot
                // The ERP payload carries no slot id; the start is unique per day column.
                key={slot.raw.start}
                time={slot.time}
                type={slot.type}
                price={slot.price}
                disabled={slot.disabled}
                capacity={
                  slot.spotsLeft ? _t('features.weekpicker.TimeSlot.spotsLeft', { count: slot.spotsLeft }) : undefined
                }
                isNarrow={isNarrow}
                onClick={() => onSelectSlot(slot.raw)}
              />
            ))}
          </div>
        );
      })}
    </div>
  );

  // In-grid loading — the week header and its arrows stay live so the visitor can
  // keep navigating, unlike the full-view PickTimeLoading the first fetch uses.
  const loadingState = (
    <div className="gap-lg flex flex-1 flex-col items-center justify-center" role="status" aria-live="polite">
      <LoadingSpinner loop style={{ width: 24, height: 24 }} />
      <p className="text-m text-fg_primary text-center">{_t('features.weekpicker.PickTimeLoading.loadingLabel')}</p>
    </div>
  );

  // Empty week — a "no slots this week" message with the next-available date,
  // a jump CTA (legacy "Visa nästa lediga tid"), and a waitlist button when the
  // consumer opts in; the consumer resolves the next-available week and
  // waitlist eligibility.
  const nextAvailableDateLabel = model.nextAvailableDate
    ? `${fmt(model.nextAvailableDate, 'D MMM YYYY')}, ${fmt(model.nextAvailableDate, 'dddd')}`
    : undefined;
  const emptyState = (
    <div className="p-lg gap-lg flex flex-1 flex-col items-center justify-center text-center">
      <p className="text-fg_primary text-l">
        {/* The last-minute sentence ends in the link, so it is only whole with a URL to hang it on. */}
        {isSmt && regularTimesUrl ? (
          <>
            {_t('notAvailableSistaminutentider')}
            <a href={regularTimesUrl} target="_blank" rel="noreferrer" className="underline">
              Bokadirekt
            </a>
          </>
        ) : (
          _t('features.weekpicker.PickTimeWeekView.noSlotsThisWeek')
        )}
        {nextAvailableDateLabel && (
          <>
            {_t('features.weekpicker.PickTimeWeekView.nextAvailableIs')}
            {': '}
            <span className="font-semibold">{nextAvailableDateLabel}</span>
          </>
        )}
      </p>
      <div className={classnames('gap-sm flex items-center', isNarrow ? 'flex-col' : 'flex-row')}>
        {showWaitlist && (
          <Button variant="secondary" onClick={onOpenWaitlist} className="min-w-[200px]">
            {_t('features.weekpicker.PickTimeWeekView.waitlist')}
          </Button>
        )}
        {nextAvailableDateLabel && (
          <Button variant="primary" onClick={onJumpToNextAvailable} className="min-w-[200px]">
            {_t('features.weekpicker.PickTimeWeekView.jumpToNextAvailable')}
          </Button>
        )}
      </div>
    </div>
  );

  return (
    <div role="region" aria-label={title} className="flex h-full w-full flex-col">
      <div
        className={classnames(
          'flex min-h-0 flex-1 flex-col',
          // Narrow puts the header on the page surface and gives the grid alone a surface, so
          // there is no card enclosing both — the card is the desktop shape only.
          !isNarrow && 'bg-surface_l2 border-border_regular overflow-hidden rounded-lg border',
        )}>
        <WeekHeader
          title={title}
          subtitle={subtitle}
          isNarrow={isNarrow}
          prevDisabled={!canGoPrev}
          nextDisabled={!canGoNext}
          onPrev={onPrev}
          onNext={onNext}
        />
        <div
          className={classnames(
            'flex min-h-0 flex-1 flex-col overflow-y-auto overflow-x-clip',
            isNarrow && 'bg-surface_l2 border-border_regular border-y',
          )}>
          {headerRow}
          {isLoading && loadingState}
          {!isLoading && (state === 'empty' ? emptyState : columns)}
        </div>
      </div>
    </div>
  );
};

export default PickTimeWeekView;
